import * as React from "react";
import {
  Button,
  Persona,
  Card,
  CardHeader,
  Text,
  Spinner,
  makeStyles,
  tokens,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
} from "@fluentui/react-components";
import {
  PersonRegular,
  SignOutRegular,
  PersonCircleRegular,
  ChevronDownRegular,
} from "@fluentui/react-icons";
import { useAuth } from "../../auth";

const useStyles = makeStyles({
  container: {
    padding: "16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  loginContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    padding: "20px",
  },
  loginText: {
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  userCard: {
    width: "100%",
  },
  userMenu: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: tokens.borderRadiusMedium,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: "8px",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
  },
  userName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  userEmail: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export const AuthStatus: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isLoading, user, error, login, logout } = useAuth();

  const handleLogin = async () => {
    try {
      await login();
    } catch (err) {
      // Error is already handled in the context
      console.error("Login failed:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loginContainer}>
          <Spinner size="medium" label="Authenticating..." />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.loginContainer}>
          <PersonCircleRegular style={{ fontSize: 48, color: tokens.colorNeutralForeground3 }} />
          <Text className={styles.loginText}>Please sign in to access all features</Text>
          <Button appearance="primary" icon={<PersonRegular />} onClick={handleLogin}>
            Sign In with Keycloak
          </Button>
          {error && <Text className={styles.errorText}>{error}</Text>}
        </div>
      </div>
    );
  }

  // User is authenticated
  const displayName = user?.name || user?.preferred_username || "User";
  const email = user?.email || "";

  return (
    <div className={styles.container}>
      <Card className={styles.userCard}>
        <CardHeader
          image={
            <Persona
              name={displayName}
              secondaryText={email}
              presence={{ status: "available" }}
              avatar={{ color: "colorful" }}
            />
          }
          header={
            <div className={styles.userInfo}>
              <Text className={styles.userName}>{displayName}</Text>
              {email && <Text className={styles.userEmail}>{email}</Text>}
            </div>
          }
          action={
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  icon={<ChevronDownRegular />}
                  aria-label="User menu"
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
                    Sign Out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          }
        />
      </Card>
    </div>
  );
};

export default AuthStatus;
